import { derived, get } from 'svelte/store'
import { persistentStore } from '../composables/persistentStore'
import en from './en.json'
import hr from './hr.json'

export type Locale = 'en' | 'hr'
type Messages = typeof en

const dictionaries: Record<Locale, Messages> = { en, hr }

export const LOCALES: readonly Locale[] = ['en', 'hr']

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'hr'
}

export const locale = persistentStore<Locale>({
  key: 'argus.locale',
  initial: 'en',
  validate: isLocale,
})

export type TranslateParams = Record<string, string | number>

/**
 * Resolve a dot-path key (`views.dashboard.title`) against the active locale.
 * Returns the key itself if missing — makes gaps visible in dev without
 * crashing the UI. Optional `params` fill `{name}` placeholders in the value.
 */
export function t(key: string, params?: TranslateParams): string {
  return resolve(key, dictionaries[get(locale)], params)
}

/**
 * Reactive version — re-evaluates when locale changes. Use in templates that
 * should react to language switching:
 *   {$t('nav.browse')}
 *   {$t('foo.bar', { name: 'x' })}
 */
export const tStore = derived(
  locale,
  ($locale) => (key: string, params?: TranslateParams) =>
    resolve(key, dictionaries[$locale], params),
)

function resolve(path: string, dict: Messages, params?: TranslateParams): string {
  const parts = path.split('.')
  let cursor: unknown = dict
  for (const part of parts) {
    if (typeof cursor !== 'object' || cursor === null) return path
    cursor = (cursor as Record<string, unknown>)[part]
  }
  if (typeof cursor !== 'string') return path
  if (!params) return cursor
  return cursor.replace(/\{(\w+)\}/g, (match, name) =>
    name in params ? String(params[name]) : match,
  )
}
